import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  Renderer2,
  ViewChildren,
  QueryList,
  AfterViewInit,
  inject,
  input,
  effect,
} from '@angular/core';
import { BtnGroupComponent, BtnGroupItem } from '@shared/components/btn-group/btn-group.component';
import { TreeListAction } from '@app/orders/models/dx-tree-list.model';
import { buildTreeListActionOverflowItems, getTreeListActionKey } from '../tree-list-action-mapper';
import { OrdersStatusService } from '@app/orders/services/orders-status.service';
@Component({
  selector: 'app-tree-list-actions',
  templateUrl: './tree-list-actions.component.html',
  styleUrls: ['./tree-list-actions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TreeListActionsComponent implements AfterViewInit {
  ref = inject(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly statusService = inject(OrdersStatusService);
  maxActionsToDisplay = input<number>(3);

  @ViewChildren(BtnGroupComponent) groupButtons: QueryList<BtnGroupComponent>;

  actions = input<TreeListAction[]>([]);
  fixedActions = input<TreeListAction[]>([]);

  hidden = input<boolean>(true);
  hiddenEffect = effect(() => {
    if (this.hidden()) {
      this.forceHideGroupDropdowns();
    }
  });

  data = input.required<any>();
  rowElement = input.required<HTMLElement>();
  rowIndex = input<number>();

  readonly visibleFixedActions = computed(() =>
    (this.fixedActions() ?? []).filter((action) => {
      return !action?.hidden?.(this.data());
    }),
  );

  private readonly fixedActionKeys = computed(() => {
    const fixed = this.visibleFixedActions();
    return new Set<string>(
      fixed
        .map((a) => getTreeListActionKey(a))
        .filter((key): key is string => typeof key === 'string' && key.length > 0),
    );
  });

  readonly visibleActions = computed(() =>
    (this.actions() ?? []).filter((action) => {
      this.statusService.productPrintableVersion();
      if (action?.hidden?.(this.data())) return false;
      const key = getTreeListActionKey(action);
      if (!key) return true;
      return !this.fixedActionKeys().has(key);
    }),
  );

  readonly primaryActions = computed(() => this.visibleActions().slice(0, this.maxActionsToDisplay()));

  readonly overflowItems = computed(() =>
    this.visibleActions()
      .slice(this.maxActionsToDisplay())
      .flatMap((action) => this.buildOverflowItems(action)),
  );

  ngAfterViewInit(): void {
    this.setHostStyle();
  }

  public setHostStyle() {
    this.renderer.setStyle(this.ref?.nativeElement, 'top', `${this.rowElement()?.offsetTop}px`);
    this.renderer.setStyle(this.ref?.nativeElement, 'height', `${this.rowElement()?.offsetHeight}px`);
  }

  private forceHideGroupDropdowns() {
    if (!!this.groupButtons?.length) {
      this.groupButtons.forEach((group) => group.hideButtonGroupMenu());
    }
  }

  private buildOverflowItems(action: TreeListAction): BtnGroupItem[] {
    const actionLabel = action?.contextLabel?.(this.data()) ?? action?.label ?? '';
    return buildTreeListActionOverflowItems({
      action,
      context: this.data(),
      label: actionLabel,
      onAction: () => action.command(this.data()),
    });
  }
}


